/**
 * Utilidades de identidade Mercado Livre — SKU e URLs de anúncio.
 * Unifica os clusters B-4/E-7 (normalizeSku) e E-8 (19 URLs em 11 arquivos).
 */

import { LEGACY_DECODED_SKU_KEYS } from '../constants/skuLegacy';

/**
 * Normaliza SKU pra chave de comparação/lookup: trim + UPPERCASE, SEM decode.
 * Espelha o `NormalizeSku` do backend (`core/utils/identifiers.js`).
 *
 * O `decodeURIComponent` antigo saiu em 2026-09-25: o ML manda o `%30` literal
 * e a função não era idempotente (`%2530` → `%30` → `0`). As 9 chaves que o
 * decode já tinha gravado decodificadas (`40X40T%30` → `40X40T0`) continuam
 * casando pelo mapa congelado `LEGACY_DECODED_SKU_KEYS` — até o rename desses
 * SKUs, quando o mapa sai dos dois lados.
 * Vazio/nulo → ''.
 */
export function NormalizeSku(value) {
    const sku = String(value ?? '')
        .trim()
        .toUpperCase();
    return LEGACY_DECODED_SKU_KEYS.get(sku) ?? sku;
}

const ML_BASE = 'https://www.mercadolivre.com.br';

/**
 * Builders de URL do painel de anúncios do ML (Seller Center). Todas recebem
 * o MLB (item_id) — exceto `catalog`, que recebe o catalog_product_id.
 */
export const MLB_URLS = {
    /** Busca geral de anúncios: /anuncios?search=MLB… */
    search: (itemId) => `${ML_BASE}/anuncios?search=${encodeURIComponent(itemId ?? '')}`,
    /** Listagem com sort default: /anuncios/lista?page=1&search=MLB…&sort=DEFAULT */
    listing: (itemId) => `${ML_BASE}/anuncios/lista?page=1&search=${encodeURIComponent(itemId ?? '')}&sort=DEFAULT`,
    /** Central de promoções do anúncio: /anuncios/lista/promos?… */
    promos: (itemId) => `${ML_BASE}/anuncios/lista/promos?page=1&search=${encodeURIComponent(itemId ?? '')}`,
    /** Competição de catálogo (buybox): /anuncios/lista/catalogo/competition?… */
    competition: (itemId) =>
        `${ML_BASE}/anuncios/lista/catalogo/competition?page=1&search=${encodeURIComponent(itemId ?? '')}`,
    /** Space management (brand store): /anuncios/lista/space_management?… */
    spaceManagement: (itemId) =>
        `${ML_BASE}/anuncios/lista/space_management?search=${encodeURIComponent(itemId ?? '')}`,
    /** Edição do anúncio no Seller Center: /anuncios/MLB…/modificar/ */
    edit: (itemId) => `${ML_BASE}/anuncios/${encodeURIComponent(itemId ?? '')}/modificar/`,
    /** Página pública do produto de catálogo: /p/{catalog_product_id} */
    catalog: (catalogProductId) => `${ML_BASE}/p/${encodeURIComponent(catalogProductId ?? '')}`,
};

/**
 * Atalhos GLOBAIS do Seller Center (não recebem MLB) — o menu "ML".
 *
 * União de TODOS os hyperlinks para o Mercado Livre que existiam espalhados
 * pelo Campaign Manager (`meli_manager/public/`), v1 e v2: products.html,
 * products-v2.html, campaigns.html, campaigns-v2.html, products-campaigns.html,
 * index.html e notifications.html. Cada página tinha o seu próprio dropdown com
 * um subconjunto diferente; aqui viram uma lista só, agrupada por tema.
 *
 * Ficam ao lado de `MLB_URLS` de propósito: URL do ML mora NESTE arquivo, nunca
 * solta dentro de componente (CLAUDE.md §4).
 *
 * NÃO entrou: o link de Product Ads do notifications.html, que era um
 * `mercadolibre.com/jms/.../login/<blob>/seller-ads/list` — URL de login com
 * payload embutido, que expira e não serve para outra conta. O destino canônico
 * (`/publicidade/product-ads/campaigns`) já está no grupo Publicidade.
 */
export const ML_SHORTCUT_GROUPS = [
    {
        id: 'anuncios',
        label: 'Anúncios',
        links: [
            { id: 'listings', label: 'Meus anúncios', href: `${ML_BASE}/anuncios/lista` },
            // A rota /listagem aparecia só no notifications.html; é a mesma
            // listagem por outro caminho, mantida porque o dono usa as duas.
            { id: 'listings-alt', label: 'Listagem de anúncios', href: `${ML_BASE}/anuncios/listagem` },
            { id: 'catalog-competition', label: 'Competição de catálogo', href: `${ML_BASE}/anuncios/lista/catalogo` },
        ],
    },
    {
        id: 'ofertas',
        label: 'Ofertas e campanhas',
        links: [
            { id: 'promotions-catalog', label: 'Ofertas e descontos', href: `${ML_BASE}/seller-promotions/catalog` },
            {
                id: 'promotions-dashboard',
                label: 'Dashboard de promoções',
                href: `${ML_BASE}/seller-promotions/dashboard`,
            },
            { id: 'seller-deals', label: 'Ofertas do vendedor', href: `${ML_BASE}/ofertas/vendedor` },
        ],
    },
    {
        id: 'vendas',
        label: 'Vendas',
        links: [
            { id: 'sales', label: 'Vendas', href: `${ML_BASE}/vendas/lista` },
            { id: 'sales-alt', label: 'Listagem de vendas', href: `${ML_BASE}/vendas/listagem` },
        ],
    },
    {
        id: 'publicidade',
        label: 'Publicidade',
        links: [{ id: 'product-ads', label: 'Product Ads', href: `${ML_BASE}/publicidade/product-ads/campaigns` }],
    },
    {
        id: 'conta',
        label: 'Conta',
        links: [
            { id: 'metrics', label: 'Métricas', href: `${ML_BASE}/minha-conta/metricas` },
            { id: 'summary', label: 'Resumo da conta', href: `${ML_BASE}/minha-conta/resumo` },
        ],
    },
];

/**
 * URL de imagem do ML segura pra página HTTPS. O `thumbnail` de /items vem em
 * `http://http2.mlstatic.com/...`; navegadores bloqueiam (ou precisam
 * "auto-upgrade") conteúdo misto. O mesmo host responde em https.
 *
 * @param {string|null|undefined} url
 * @returns {string|null}
 */
export function SecureMlImageUrl(url) {
    const raw = String(url ?? '').trim();
    if (!raw) {
        return null;
    }
    return raw.replace(/^http:\/\//i, 'https://');
}
